import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtNumber, fmtPct, severityClass, statusPill } from "../lib/utils";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Line, LineChart, ReferenceLine,
} from "recharts";
import { ArrowLeft, Sparkles, AlertTriangle, FileText, ShieldCheck, Target, BarChart3, Lock, UserCog, Pencil, X, Save } from "lucide-react";
import AdviserDrawer from "../components/AdviserDrawer";
import EvidencePanel from "../components/EvidencePanel";
import ActivityRecordsTable from "../components/ActivityRecordsTable";
import GhgInventoryWidget from "../components/GhgInventoryWidget";
import EntityConsolidationCard from "../components/EntityConsolidationCard";

const EDITOR_ROLES = new Set(["tenant_admin", "esg_lead", "platform_admin"]);

const CARBON_KPI_IDS = new Set([
  "scope1_emissions", "scope2_emissions", "scope3_emissions", "carbon_intensity",
]);
const ACTIVITY_KPI_IDS = new Set([
  "scope1_emissions", "scope2_emissions", "scope3_emissions",
  "energy_total", "energy_renewable_pct", "ewaste_recycled",
]);

function ScopeTile({ label, value, color, total, testid }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="card-soft p-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="dot" style={{ background: color }} />
          <span className="text-xs font-semibold">{label}</span>
        </div>
        <span className="text-[10px] text-ink-soft font-mono">{pct.toFixed(1)}%</span>
      </div>
      <div className="font-mono text-lg mt-1">{fmtNumber(value)}<span className="text-xs text-ink-soft"> tCO₂e</span></div>
      <div className="h-1.5 bg-surface rounded-full overflow-hidden mt-2">
        <div className="h-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

export default function KpiDetail() {
  const { kpiId } = useParams();
  const { user } = useAuth();
  const canEdit = EDITOR_ROLES.has(user?.role);
  const [data, setData] = useState(null);
  const [adviserOpen, setAdviserOpen] = useState(false);
  const [ownershipOpen, setOwnershipOpen] = useState(false);

  const reload = async () => {
    const r = await api.get(`/kpis/${kpiId}/detail`);
    setData(r.data);
  };

  useEffect(() => {
    api.get(`/kpis/${kpiId}/detail`).then(r => setData(r.data));
  }, [kpiId]);

  if (!data) return <div className="p-6 text-ink-soft">Loading KPI…</div>;
  const { kpi, series, baseline_value, current_value, target_value, target_year, target_notes, target_custom, owner, anomalies, initiatives, evidence, benchmark, total_emissions, scope_breakdown, ghg_inventory, entity_consolidation } = data;
  const chartData = series.map(s => ({ period: s.period, value: s.value, confidence: s.confidence }));
  const showCarbonExtras = CARBON_KPI_IDS.has(kpi.id);
  const showActivityTable = ACTIVITY_KPI_IDS.has(kpi.id);

  const direction = kpi.direction === "lower_is_better";
  const progress =
    target_value === baseline_value ? 100 :
    Math.max(0, Math.min(120,
      ((current_value - baseline_value) / (target_value - baseline_value)) * 100
    ));

  return (
    <div className="p-6 space-y-5" data-testid="kpi-detail-root">
      <Link to="/scorecard" className="text-xs text-ink-soft inline-flex items-center gap-1" data-testid="back-link">
        <ArrowLeft size={12}/> Back to Scorecard
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="section-label">{kpi.material_issue}</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1" data-testid="kpi-name">{kpi.name}</h1>
          <p className="text-sm text-ink-soft max-w-2xl">{kpi.description}</p>
          <div className="mt-2 flex flex-wrap gap-1">
            {kpi.standards.map(s => <span key={s} className="pill">{s}</span>)}
          </div>
        </div>
        <button
          data-testid="explain-with-ai-button"
          onClick={() => setAdviserOpen(true)}
          className="btn btn-primary"
        >
          <Sparkles size={14}/> Explain with AI
        </button>
      </div>

      {/* Top metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="section-label">Current</div>
          <div className="metric-value mt-1" data-testid="metric-current">{fmtNumber(current_value)} <span className="text-sm font-normal text-ink-soft">{kpi.unit}</span></div>
          <div className="text-xs text-ink-soft mt-1">Period {series[series.length-1]?.period}</div>
        </div>
        <div className="card p-4">
          <div className="section-label">Baseline</div>
          <div className="metric-value mt-1">{fmtNumber(baseline_value)} <span className="text-sm font-normal text-ink-soft">{kpi.unit}</span></div>
          <div className="text-xs text-ink-soft mt-1">{series[0]?.period}</div>
        </div>
        <div className="card p-4">
          <div className="section-label">Target</div>
          <div className="metric-value mt-1 text-brand">{fmtNumber(target_value)} <span className="text-sm font-normal text-ink-soft">{kpi.unit}</span></div>
          <div className="text-xs text-ink-soft mt-1">{direction ? "lower is better" : "higher is better"}</div>
        </div>
        <div className="card p-4">
          <div className="section-label">Progress to Target</div>
          <div className="metric-value mt-1">{Math.round(progress)}%</div>
          <div className="mt-2 h-1.5 bg-surface rounded-full overflow-hidden">
            <div style={{width: `${Math.min(100, progress)}%`}} className="h-full bg-brand"/>
          </div>
        </div>
      </div>

      {/* Inline KPI Ownership & Target card */}
      <div className="card p-4" data-testid="kpi-ownership-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-9 w-9 rounded-md bg-brand text-white flex items-center justify-center shrink-0">
              <UserCog size={14}/>
            </div>
            <div className="min-w-0">
              <div className="section-label">KPI ownership & target</div>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm">
                <span>
                  Owner:{" "}
                  {owner ? (
                    <>
                      <span className="font-semibold" data-testid="owner-name">{owner.name}</span>
                      <span className="text-ink-soft ml-1 font-mono text-xs">{owner.email}</span>
                    </>
                  ) : (
                    <span className="pill pill-warn text-[10px]" data-testid="owner-unassigned">Unassigned</span>
                  )}
                </span>
                <span className="text-ink-soft">·</span>
                <span>
                  Target:{" "}
                  <span className="font-semibold font-mono" data-testid="target-display">
                    {fmtNumber(target_value)} {kpi.unit}
                  </span>
                  {target_year && (
                    <span className="text-ink-soft text-xs ml-1">by {target_year}</span>
                  )}
                  {target_custom ? (
                    <span className="pill pill-success text-[9px] ml-2">Custom</span>
                  ) : (
                    <span className="pill text-[9px] ml-2" title="Synthesized from organisational delta">Default</span>
                  )}
                </span>
              </div>
              {target_notes && (
                <div className="text-xs text-ink-soft mt-1 italic max-w-2xl">"{target_notes}"</div>
              )}
            </div>
          </div>
          {canEdit && (
            <button
              data-testid="open-ownership-editor"
              onClick={() => setOwnershipOpen(true)}
              className="btn btn-secondary"
            >
              <Pencil size={12}/> Edit ownership / target
            </button>
          )}
        </div>
      </div>

      {/* Carbon footprint context (Scope 1+2+3 totals + scope breakdown) */}
      {showCarbonExtras && total_emissions !== null && scope_breakdown && (
        <div className="card p-4" data-testid="carbon-context-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="section-label">Carbon footprint context · {ghg_inventory?.period}</div>
              <div className="text-xs text-ink-soft mt-0.5">
                Total greenhouse gas emissions across all three scopes for the current period.
              </div>
            </div>
            <div className="text-right">
              <div className="dlabel">Total Scope 1+2+3</div>
              <div className="metric-value text-brand" data-testid="total-emissions">
                {fmtNumber(total_emissions)} <span className="text-sm font-normal text-ink-soft">tCO₂e</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            <ScopeTile label="Scope 1" value={scope_breakdown.scope1} color="#064E3B" total={total_emissions} testid="scope-tile-1"/>
            <ScopeTile label="Scope 2" value={scope_breakdown.scope2} color="#10B981" total={total_emissions} testid="scope-tile-2"/>
            <ScopeTile label="Scope 3" value={scope_breakdown.scope3} color="#A7F3D0" total={total_emissions} testid="scope-tile-3"/>
          </div>
        </div>
      )}

      {/* GHG Inventory widget */}
      {showCarbonExtras && ghg_inventory && (
        <GhgInventoryWidget inventory={ghg_inventory} />
      )}

      {/* Row-by-row consolidation per Site (scope KPIs only) */}
      {entity_consolidation && (
        <EntityConsolidationCard data={entity_consolidation} />
      )}

      {/* Chart */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="section-label">Trend</div>
            <div className="text-sm text-ink-soft mt-1">Quarterly measurements with target reference line</div>
          </div>
          <div className="text-xs flex gap-2 items-center">
            <span className="dot bg-brand"/> Measured
            <span className="ml-2 inline-block w-3 border-t border-dashed border-status-info"/> Target
          </div>
        </div>
        <div className="h-[280px] mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#E2E8F0" vertical={false}/>
              <XAxis dataKey="period" tick={{fontSize:11, fill:'#64748B'}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11, fill:'#64748B'}} axisLine={false} tickLine={false} width={60}/>
              <Tooltip />
              <ReferenceLine y={target_value} stroke="#3B82F6" strokeDasharray="4 4" label={{ value: 'Target', position: 'right', fill: '#3B82F6', fontSize: 10 }}/>
              <ReferenceLine y={baseline_value} stroke="#94A3B8" strokeDasharray="2 2" label={{ value: 'Baseline', position: 'right', fill: '#94A3B8', fontSize: 10 }}/>
              <Line type="monotone" dataKey="value" stroke="#064E3B" strokeWidth={2} dot={{r:3, fill:'#064E3B'}} activeDot={{r:5}}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Anomalies + Initiatives */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-4" data-testid="kpi-anomalies-card">
          <div className="section-label flex items-center gap-2"><AlertTriangle size={12}/> Anomalies</div>
          {anomalies.length === 0 ? (
            <div className="mt-2 text-sm text-ink-soft">No anomalies. Data quality looks clean.</div>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {anomalies.map(a => (
                <li key={a.id} className="py-3 text-sm" data-testid={`kpi-anom-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={severityClass(a.severity)}>{a.severity}</span>
                      <span className="font-medium">{a.type.replaceAll("_", " ")}</span>
                      <span className="text-ink-soft">· {a.period}</span>
                    </div>
                    <span className="text-[11px] text-ink-soft">{a.status}</span>
                  </div>
                  <div className="text-ink-soft mt-1">{a.recommended_action}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card p-4" data-testid="kpi-initiatives-card">
          <div className="section-label flex items-center gap-2"><Target size={12}/> Initiatives</div>
          {initiatives.length === 0 ? (
            <div className="mt-2 text-sm text-ink-soft">No initiatives linked to this KPI yet.</div>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {initiatives.map(i => (
                <li key={i.id} className="py-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{i.name}</div>
                    <span className={statusPill(i.status)}>{i.status.replaceAll("_", " ")}</span>
                  </div>
                  <div className="text-ink-soft mt-1">{i.expected_impact}</div>
                  <div className="text-[11px] text-ink-soft mt-1">
                    Owner {i.owner || "—"} · target {i.target_completion || "—"} · CapEx ${fmtNumber(i.capex_usd, 0)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <AdviserDrawer open={adviserOpen} onClose={() => setAdviserOpen(false)} kpiId={kpiId} />

      {/* Benchmark + Evidence row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-4" data-testid="kpi-benchmark-card">
          <div className="flex items-center justify-between">
            <div className="section-label flex items-center gap-2"><BarChart3 size={12}/> Cohort Benchmark</div>
            <Link to="/benchmarks" className="text-xs text-brand font-semibold">All benchmarks →</Link>
          </div>
          {!benchmark || benchmark.suppressed ? (
            <div className="text-xs text-ink-soft mt-3 flex items-center gap-2"><Lock size={12}/> Cohort suppressed under k-anonymity (k≥{benchmark?.k_anonymity || 2}).</div>
          ) : (
            <div className="mt-3">
              <div className="grid grid-cols-5 gap-2 text-center text-[11px]">
                {[
                  { l: "Min", v: benchmark.min },
                  { l: "P25", v: benchmark.p25 },
                  { l: "Median", v: benchmark.p50, hl: true },
                  { l: "P75", v: benchmark.p75 },
                  { l: "Max", v: benchmark.max },
                ].map(x => (
                  <div key={x.l} className="card-soft p-2">
                    <div className="dlabel">{x.l}</div>
                    <div className={"font-mono mt-1 " + (x.hl ? "font-bold text-brand" : "")}>{fmtNumber(x.v)}</div>
                  </div>
                ))}
              </div>
              {benchmark.tenant_value != null && (
                <div className="mt-4">
                  <div className="text-xs text-ink-soft mb-1">Your distribution</div>
                  <div className="relative h-4 bg-surface border border-border rounded-full overflow-hidden">
                    {(() => {
                      const range = benchmark.max - benchmark.min || 1;
                      const yourPct = ((benchmark.tenant_value - benchmark.min) / range) * 100;
                      const p25 = ((benchmark.p25 - benchmark.min) / range) * 100;
                      const p75 = ((benchmark.p75 - benchmark.min) / range) * 100;
                      const p50 = ((benchmark.p50 - benchmark.min) / range) * 100;
                      return (
                        <>
                          <div className="absolute top-0 bottom-0 bg-brand-100" style={{left:`${p25}%`, width:`${Math.max(0,p75-p25)}%`}}/>
                          <div className="absolute top-0 bottom-0 w-[2px] bg-brand" style={{left:`${p50}%`}}/>
                          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 border-white bg-ink shadow-card" style={{left:`${Math.max(0,Math.min(100,yourPct))}%`}}/>
                        </>
                      );
                    })()}
                  </div>
                  <div className="flex justify-between text-[10px] text-ink-soft mt-1">
                    <span>{fmtNumber(benchmark.min)} {benchmark.unit}</span>
                    <span>{fmtNumber(benchmark.max)} {benchmark.unit}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <span className="dlabel">Your position</span>
                    <span className={
                      "pill " + (
                        benchmark.your_position === "top_quartile" ? "pill-success" :
                        benchmark.your_position === "above_median" ? "pill-info" :
                        benchmark.your_position === "below_median" ? "pill-warn" : "pill-error"
                      )
                    }>{benchmark.your_position?.replaceAll("_", " ") || "—"}</span>
                    <span className="text-ink-soft text-xs">· {benchmark.your_percentile}th percentile · cohort n={benchmark.cohort_size}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="text-[11px] text-ink-soft mt-4 flex items-center gap-1">
            <Lock size={11}/> k-anonymity ≥ {benchmark?.k_anonymity || 2} · 5/95 winsorisation · no peer identities revealed
          </div>
        </div>

        <div className="card p-4">
          <EvidencePanel kpiId={kpiId} compact />
        </div>
      </div>

      {/* Source-of-truth: activity records */}
      {showActivityTable && (
        <ActivityRecordsTable kpiId={kpiId} />
      )}

      {ownershipOpen && (
        <InlineOwnershipEditor
          kpi={kpi}
          owner={owner}
          targetValue={target_value}
          targetCustom={target_custom}
          targetYear={target_year}
          targetNotes={target_notes}
          baselineValue={baseline_value}
          currentValue={current_value}
          onClose={() => setOwnershipOpen(false)}
          onSaved={async () => { setOwnershipOpen(false); await reload(); }}
        />
      )}
    </div>
  );
}

function InlineOwnershipEditor({ kpi, owner, targetValue, targetCustom, targetYear, targetNotes, baselineValue, currentValue, onClose, onSaved }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    owner_user_id: owner?.id || "",
    target_value: targetCustom && targetValue != null ? targetValue : "",
    target_year: targetYear || "",
    target_notes: targetNotes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/kpi-ownership/users").then(r => setUsers(r.data.users || []));
  }, []);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(`/admin/kpi-ownership/${kpi.id}`, {
        owner_user_id: form.owner_user_id || null,
        target_value: form.target_value === "" ? null : Number(form.target_value),
        target_year: form.target_year === "" ? null : Number(form.target_year),
        target_notes: form.target_notes || null,
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="inline-ownership-editor">
      <div className="card w-full max-w-lg bg-white max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="section-label flex items-center gap-2"><UserCog size={12}/> Ownership & target</div>
            <div className="font-semibold mt-1">{kpi.name}</div>
            <div className="text-xs text-ink-soft">unit <span className="font-mono">{kpi.unit}</span></div>
          </div>
          <button data-testid="inline-editor-close" onClick={onClose} className="btn btn-ghost"><X size={14}/></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div>
            <label className="dlabel">Owner</label>
            <select
              data-testid="inline-owner-select"
              value={form.owner_user_id}
              onChange={e => setForm({ ...form, owner_user_id: e.target.value })}
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"
            >
              <option value="">— unassigned —</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.name} · {u.role} · {u.email}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="dlabel">Target value <span className="font-mono text-ink-soft">({kpi.unit})</span></label>
              <input
                data-testid="inline-target-value"
                type="number" step="any"
                value={form.target_value}
                onChange={e => setForm({ ...form, target_value: e.target.value })}
                className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <label className="dlabel">Target year</label>
              <input
                data-testid="inline-target-year"
                type="number" min="1990" max="2100"
                value={form.target_year}
                onChange={e => setForm({ ...form, target_year: e.target.value })}
                className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm font-mono"
              />
            </div>
          </div>
          <div>
            <label className="dlabel">Notes (cited in reports)</label>
            <textarea
              data-testid="inline-target-notes"
              value={form.target_notes}
              onChange={e => setForm({ ...form, target_notes: e.target.value })}
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm h-20"
            />
          </div>
          <div className="card-soft p-3 text-xs text-ink-soft">
            <span className="font-mono">Baseline {baselineValue != null ? fmtNumber(baselineValue) : "—"} {kpi.unit}</span>
            <span className="mx-1">·</span>
            <span className="font-mono">Current {currentValue != null ? fmtNumber(currentValue) : "—"} {kpi.unit}</span>
          </div>
          {err && <div className="text-status-error text-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button onClick={onClose} className="btn btn-ghost">Cancel</button>
            <button data-testid="inline-save" disabled={busy} onClick={save} className="btn btn-primary">
              <Save size={12}/> {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
